/**
 * component-rail-pager.js
 * Prev / next / "1 / 7" controls for a .rail scroll-snap carousel.
 *
 * PROGRESSIVE ENHANCEMENT, NOT A CAROUSEL LIBRARY.
 *
 * The rail is already a native horizontal scroll-snap container: it works with
 * touch, trackpad, shift+wheel and keyboard before any of this runs. This adds
 * pointer affordances on top — the arrows and the counter — because on a
 * desktop mouse there is no visible cue that the row scrolls at all. That is
 * the one real gap in the native behaviour.
 *
 * So the controls are rendered hidden and only revealed here. If the script
 * fails, the customer gets a plain scrollable rail rather than dead buttons.
 *
 * Page size is derived from measured geometry rather than a configured column
 * count, because .rail sizes its items with a percentage that changes at the
 * 750px breakpoint. Reading it back from the DOM means the counter cannot
 * disagree with what is on screen.
 */
(() => {
  if (customElements.get('rail-pager')) return;

  class RailPager extends HTMLElement {
    connectedCallback() {
      this.rail = document.getElementById(this.dataset.railId);
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.current = this.querySelector('[data-rail-current]');
      this.total = this.querySelector('[data-rail-total]');

      if (!this.rail || !this.prev || !this.next) return;

      // Nothing to page through — a rail that fits entirely on screen should
      // not show controls implying hidden content.
      if (this.rail.scrollWidth <= this.rail.clientWidth + 2) {
        this.hidden = true;
        return;
      }

      this.hidden = false;

      this.onPrev = () => this.page(-1);
      this.onNext = () => this.page(1);
      this.prev.addEventListener('click', this.onPrev);
      this.next.addEventListener('click', this.onNext);

      this.onScroll = window.Theme.debounce(() => this.sync(), 100);
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });

      this.onResize = window.Theme.debounce(() => this.sync(), 200);
      window.addEventListener('resize', this.onResize);

      this.sync();
    }

    disconnectedCallback() {
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    /** Width of one "page" — how far one arrow press travels. */
    pageWidth() {
      const first = this.rail.firstElementChild;
      if (!first) return this.rail.clientWidth;
      const itemWidth = first.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      const per = Math.max(1, Math.round(this.rail.clientWidth / (itemWidth + gap)));
      return per * (itemWidth + gap);
    }

    pageCount() {
      const w = this.pageWidth();
      if (!w) return 1;
      return Math.max(1, Math.ceil(this.rail.scrollWidth / w));
    }

    page(direction) {
      // base.css neutralises motion globally under prefers-reduced-motion, so
      // a smooth scroll here would be the one animation in the theme that
      // ignores the preference. Jump instead.
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.rail.scrollBy({
        left: this.pageWidth() * direction,
        behavior: reduced ? 'auto' : 'smooth'
      });
    }

    sync() {
      const total = this.pageCount();

      // Index comes from SCROLL PROGRESS, not from scrollLeft / pageWidth.
      //
      // The last page is almost always partial: with 7 cards at 23% the rail
      // is 2080px wide in a 1232px window, so the furthest it can scroll is
      // 848px while a "page" is 1197px. Dividing one by the other never
      // reaches 2, so the counter sat on "1/2" at every scroll position
      // including the very end — the arrows worked and the number did not.
      //
      // Mapping progress across the scrollable range instead guarantees the
      // first position reads 1 and the last reads `total`, whatever the
      // leftover on the final page.
      const maxScroll = this.rail.scrollWidth - this.rail.clientWidth;
      const progress = maxScroll > 0 ? this.rail.scrollLeft / maxScroll : 0;
      const index = Math.min(total, Math.round(progress * (total - 1)) + 1);

      if (this.current) this.current.textContent = index;
      if (this.total) this.total.textContent = total;

      // Disabled rather than hidden at the ends: a control that disappears
      // shifts the row underneath it every time you reach an edge.
      const atStart = this.rail.scrollLeft <= 2;
      const atEnd = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 2;
      this.prev.disabled = atStart;
      this.next.disabled = atEnd;
    }
  }

  customElements.define('rail-pager', RailPager);
})();
